






































































































import { Component, Prop, Vue } from 'vue-property-decorator'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'

export interface PerSubjectOverviewTableData {
    [subjectId: string]: {
        subject: User|Branch,
        achievedSalesQuantity: number,
        involvedSalesQuantity: number,
        achievedSalesTurnover: number,
        achievedMortgageQuantity: number,
        involvedMortgageQuantity: number,
        achievedMortgageTurnover: number,
    }
}

@Component
export default class PerSubjectOverviewTable extends Vue {
    @Prop({ required: true, default: () => [] }) readonly data!: PerSubjectOverviewTableData
    @Prop() readonly subject!: User|Branch
    @Prop() readonly subjectLabel!: string
    @Prop({ default: true }) readonly showInvolvedQuantity!: boolean

    get subjectIds(): string [] {
        return Object.keys(this.data)
    }

    get sortedSubjectIds(): string[] {
        return this.subjectIds
            .sort((a, b) => {
                return this.getTotalAchievedTurnover(this.data[b]) - this.getTotalAchievedTurnover(this.data[a])
            })
            .filter(subjectId => !this.subject || this.subject.id === subjectId)
    }

    get totals() {
        const total = {
            achievedSalesQuantity: 0,
            involvedSalesQuantity: 0,
            achievedSalesTurnover: 0,
            involvedMortgageQuantity: 0,
            achievedMortgageQuantity: 0,
            achievedMortgageTurnover: 0,
        }

        Object.keys(this.data).forEach(subjectId => {
            total.achievedSalesQuantity += this.data[subjectId].achievedSalesQuantity
            total.involvedSalesQuantity += this.data[subjectId].involvedSalesQuantity
            total.achievedSalesTurnover += this.data[subjectId].achievedSalesTurnover
            total.achievedMortgageQuantity += this.data[subjectId].achievedMortgageQuantity
            total.involvedMortgageQuantity += this.data[subjectId].involvedMortgageQuantity
            total.achievedMortgageTurnover += this.data[subjectId].achievedMortgageTurnover
        })

        return total
    }

    getTotalAchievedQuantity(data: { achievedSalesQuantity: number, achievedMortgageQuantity: number }): number {
        return data.achievedSalesQuantity + data.achievedMortgageQuantity
    }

    getTotalAchievedTurnover(data: { achievedSalesTurnover: number, achievedMortgageTurnover: number }): number {
        return data.achievedSalesTurnover + data.achievedMortgageTurnover
    }

    formatSubject(subject: User|Branch): string {
        return subject.name
    }

    formatQuantity(quantity: number): string {
        if (quantity === null) {
            return ''
        }

        return (quantity || 0).toLocaleString('nl', { maximumFractionDigits: 0 })
    }

    formatTurnover(turnover: number): string {
        if (turnover === null) {
            return ''
        }

        return (turnover || 0).toLocaleString('nl', { maximumFractionDigits: 2, style: 'currency', currency: 'EUR' })
    }

    formatPercentage(percentage: number): string {
        if (percentage === null) {
            return ''
        }

        return (percentage || 0).toLocaleString('nl', { style: 'percent' })
    }

    get gridStyles(): object {
        const styles = {}

        if (this.showInvolvedQuantity) {
            styles['grid-template-columns'] = 'auto min-content min-content min-content 8rem 4rem min-content min-content min-content 8rem 4rem 8rem 4rem 9rem'
        } else {
            styles['grid-template-columns'] = 'auto min-content 8rem 4rem min-content 8rem 4rem 8rem 4rem 9rem'
        }

        return styles
    }

    headerRowClasses(classes: object = {}): object {
        return Object.assign({
            'bg-gray-200': true,
            'text-sm': true,
            'lowercase': true,
            'p-2': true,
        }, classes)
    }

    rowClasses(subjectId: string, classes: object = {}): object {
        return Object.assign({
            'border-b': true,
            'p-2': true,
            'font-bold': this.isCurrentUser(this.data[subjectId].subject),
            'text-green-500': this.isCurrentUser(this.data[subjectId].subject),
        }, classes)
    }

    footerRowClasses(classes: object = {}): object {
        return Object.assign({
            'border-b': true,
            'p-2': true,
            'font-bold': true,
        }, classes)
    }

    isCurrentUser(subject: User|Branch): boolean {
        if (subject instanceof User) {
            return this.$currentUser.id === subject.id
        }

        if (subject instanceof Branch) {
            return this.$currentUser.getAvailableBranches().some(branch => {
                return branch.id === subject.id
            })
        }

        return false
    }
}
